import type { Locale } from '@porotutu/i18n'
import { stampedAt } from '@porotutu/i18n'
import type { ListMeta } from '@porotutu/react/paging'
import { useT } from '@/session/useSession'
import { type Column, DataTable } from '../DataTable/DataTable'
import { type Change, Diff, type EnumScopes } from '../Diff/Diff'
import { EmptyState } from '../EmptyState/EmptyState'
import { Pagination } from '../Pagination/Pagination'
import { Panel } from '../Panel/Panel'
import { Badge } from '../ui/badge'

export type Entry = {
  id: string
  actor_name: string
  action_type: string
  changed_fields: string[]
  changes: Change[]
  created_at: string
}

export type Subject = { header: string; cell: (row: Entry) => React.ReactNode }

export const HISTORY_LIMIT = 25

export const historyOffsetOf = (raw: Record<string, unknown>): number | undefined => {
  const offset = Number(raw.history_offset)

  return Number.isFinite(offset) && offset > 0 ? offset : undefined
}

export const historyWindow = (offset: number | undefined) => ({
  limit: HISTORY_LIMIT,
  offset: offset ?? 0,
})

export type HistorySearch<Query> = Query & { history_offset?: number }

export const historySearchOf =
  <Query,>(listSearch: (raw: Record<string, unknown>) => Query) =>
  (raw: Record<string, unknown>): HistorySearch<Query> => ({
    ...listSearch(raw),
    history_offset: historyOffsetOf(raw),
  })

export const historyDeps = ({ search }: { search: { history_offset?: number } }) => ({
  history_offset: search.history_offset,
})

export type HistoryPageProps = {
  scope: string
  fieldScope?: string
  enums?: EnumScopes | ((row: Entry) => EnumScopes)
  heading: string
  subheading: string
  entries: Entry[]
  meta: ListMeta
  locale: Locale
  subject?: Subject
  actions?: React.ReactNode
}

export function HistoryPage({
  scope,
  fieldScope,
  enums,
  heading,
  subheading,
  entries,
  meta,
  locale,
  subject,
  actions,
}: HistoryPageProps) {
  const t = useT()
  const columns: Array<Column<Entry>> = [
    {
      key: 'when',
      header: t(`${scope}.table.when`),
      cell: (row) => stampedAt(row.created_at, locale),
    },
    {
      key: 'who',
      header: t(`${scope}.table.who`),
      cell: (row) => row.actor_name || t('audit.blank'),
    },
    {
      key: 'action',
      header: t(`${scope}.table.action`),
      cell: (row) => <Badge>{t(`${scope}.action.${row.action_type}`)}</Badge>,
    },
    ...(subject
      ? [{ key: 'subject', header: subject.header, cell: subject.cell } as Column<Entry>]
      : []),
    {
      key: 'changes',
      header: t(`${scope}.table.changes`),
      cell: (row) =>
        row.changes.length === 0 ? (
          t(`${scope}.unchanged`)
        ) : (
          <Diff
            changes={row.changes}
            scope={fieldScope}
            enums={typeof enums === 'function' ? enums(row) : enums}
            action={row.action_type as 'insert' | 'update' | 'delete'}
          />
        ),
    },
  ]

  return (
    <Panel level={1} title={heading} subtitle={subheading} actions={actions}>
      <DataTable
        columns={columns}
        rows={entries}
        rowKey={(row) => row.id}
        empty={<EmptyState>{t(`${scope}.empty`)}</EmptyState>}
      />
      <Pagination meta={meta} offsetKey="history_offset" sizes={false} />
    </Panel>
  )
}
